import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Button, Chip, IconButton, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Select, MenuItem, FormControl, InputLabel, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid,
  CircularProgress, Stack, Tooltip, Menu, useMediaQuery, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SearchIcon from '@mui/icons-material/Search';
import { getBillingList, createPayment, updatePayment, deletePayment, getBillingStats, exportBilling, getPatients } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import { useApiCall } from '../hooks/useApiCall';
import { useClinicFeatures } from '../hooks/useClinicFeatures';
import { useNavigate } from 'react-router-dom';

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const STATUS_CONFIG = {
  pendiente:   { label: 'Pendiente',   color: 'warning' },
  'seña':      { label: 'Seña',        color: 'secondary' },
  pagado:      { label: 'Pagado',      color: 'success' },
  obra_social: { label: 'Obra social', color: 'info' },
  exento:      { label: 'Exento',      color: 'default' },
};

const METHOD_LABELS = {
  efectivo:      'Efectivo',
  transferencia: 'Transferencia',
  tarjeta:       'Tarjeta',
  obra_social:   'Obra social',
};

const EMPTY_FORM = {
  patient_id: '',
  appointment_id: '',
  amount: '',
  currency: 'ARS',
  status: 'pendiente',
  payment_method: '',
  notes: '',
  paid_at: '',
};

function StatCard({ label, value, color }) {
  return (
    <Paper sx={{ p: 2.5, textAlign: 'center' }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h5" fontWeight={700} color={color || 'text.primary'}>
        {value}
      </Typography>
    </Paper>
  );
}

export default function Billing() {
  const notify = useNotification();
  const apiCall = useApiCall();
  const navigate = useNavigate();
  const { features, loaded } = useClinicFeatures();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [stats, setStats] = useState(null);

  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [searchInputKey, setSearchInputKey] = useState(0);
  const searchTimer = useRef(null);

  // date_to se extiende a las 23:59:59 de ese día — si no, un filtro "hasta
  // hoy" excluiría los pagos de hoy mismo (created_at con hora > 00:00).
  const dateParams = () => {
    const p = {};
    if (dateFrom) p.date_from = new Date(`${dateFrom}T00:00:00`).toISOString();
    if (dateTo) p.date_to = new Date(`${dateTo}T23:59:59`).toISOString();
    return p;
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(0);
      setSearch(val);
    }, 350);
  };

  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [patients, setPatients] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const openMenu = (e, row) => { setMenuAnchor(e.currentTarget); setMenuRow(row); };
  const closeMenu = () => { setMenuAnchor(null); setMenuRow(null); };

  const billingEnabled = loaded && features?.billing;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const dateP = dateParams();
      const params = { skip: page * rowsPerPage, limit: rowsPerPage, ...dateP };
      if (filterStatus) params.status = filterStatus;
      if (search) params.search = search;
      const [listRes, statsRes] = await Promise.all([
        getBillingList(params),
        getBillingStats(dateP),
      ]);
      setRows(listRes.data.items || []);
      setTotal(listRes.data.total ?? 0);
      setStats(statsRes.data);
    } catch (err) {
      setLoadError(true);
      notify(err.userMessage || 'Error al cargar facturación', 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [page, rowsPerPage, filterStatus, dateFrom, dateTo, search, notify]);

  useEffect(() => {
    if (billingEnabled) fetchData();
    else if (loaded) setLoading(false);
  }, [billingEnabled, fetchData, loaded]);

  useEffect(() => {
    getPatients({ limit: 500 }).then(r => setPatients(r.data?.items || [])).catch(() => {});
  }, []);

  const openCreate = () => { setEditRow(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (row) => {
    closeMenu();
    setEditRow(row);
    setForm({
      patient_id: row.patient_id,
      appointment_id: row.appointment_id || '',
      amount: row.amount || '',
      currency: row.currency || 'ARS',
      status: row.status,
      payment_method: row.payment_method || '',
      notes: row.notes || '',
      paid_at: row.paid_at ? row.paid_at.slice(0, 10) : '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.patient_id) { notify('Seleccioná un paciente', 'warning'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        patient_id: Number(form.patient_id),
        appointment_id: form.appointment_id ? Number(form.appointment_id) : null,
        amount: form.amount || null,
        payment_method: form.payment_method || null,
        notes: form.notes || null,
        paid_at: form.paid_at ? new Date(form.paid_at).toISOString() : null,
      };
      if (editRow) {
        await apiCall(updatePayment(editRow.id, payload), 'Error al guardar');
        notify('Pago actualizado', 'success');
      } else {
        await apiCall(createPayment(payload), 'Error al guardar');
        notify('Pago registrado', 'success');
      }
      setDialogOpen(false);
      fetchData();
    } catch {} finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    closeMenu();
    if (!window.confirm(`¿Eliminar este registro de pago?`)) return;
    try {
      await apiCall(deletePayment(row.id), 'Error al eliminar');
      notify('Registro eliminado', 'info');
      fetchData();
    } catch {}
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const exportParams = { ...dateParams() };
      if (filterStatus) exportParams.status = filterStatus;
      const res = await apiCall(exportBilling(exportParams), 'Error al exportar');
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Facturacion_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {} finally {
      setExporting(false);
    }
  };

  const set = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

  if (!loaded || loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  if (!billingEnabled) {
    return (
      <Box sx={{ maxWidth: 500, mx: 'auto', mt: 8, textAlign: 'center' }}>
        <AttachMoneyIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h5" gutterBottom>Módulo de facturación desactivado</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Este módulo está apagado para esta clínica. Un administrador puede activarlo desde Configuración → Módulos.
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/settings')}>Ir a Configuración</Button>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', mt: 8, textAlign: 'center' }}>
        <Typography variant="h6" color="error" gutterBottom>No se pudieron cargar los datos</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Verificá que el servidor esté disponible e intentá de nuevo.
        </Typography>
        <Button variant="contained" onClick={fetchData}>Reintentar</Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* En mobile el título y los botones se apretaban/superponían en una
          sola fila sin wrap — mismo criterio que PageHeader en Patients.js:
          flexWrap + los botones a ancho completo en xs para que quede todo
          legible y tocable en vez de comprimido. */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
        <Typography variant="h4">Facturación</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="outlined"
            startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={handleExport}
            disabled={exporting}
            fullWidth={isMobile}
          >
            Exportar Excel
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} fullWidth={isMobile}>
            Registrar pago
          </Button>
        </Stack>
      </Box>

      {/* Stats — "Total cobrado" incluye señas (son dinero ya cobrado, no
          "pendiente" en el sentido de "nada cobrado todavía"), con una
          card aparte para ver cuánto de eso son señas con saldo abierto. */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 2.4 }}>
            <StatCard label="Total cobrado" value={`$${(stats.total_cobrado_real ?? stats.total_pagado).toLocaleString('es-AR')}`} color="success.main" />
          </Grid>
          {stats.total_seña > 0 && (
            <Grid size={{ xs: 6, sm: 2.4 }}>
              <StatCard label="Señas con saldo pendiente" value={`$${stats.total_seña.toLocaleString('es-AR')}`} color="secondary.main" />
            </Grid>
          )}
          <Grid size={{ xs: 6, sm: 2.4 }}>
            <StatCard label="Pendiente de cobro" value={`$${stats.total_pendiente.toLocaleString('es-AR')}`} color="warning.main" />
          </Grid>
          <Grid size={{ xs: 6, sm: 2.4 }}>
            <StatCard label="Obra social" value={`$${stats.total_obra_social.toLocaleString('es-AR')}`} color="info.main" />
          </Grid>
          <Grid size={{ xs: 6, sm: 2.4 }}>
            <StatCard label="Registros totales" value={stats.total_registros} />
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          key={searchInputKey}
          placeholder="Buscar por paciente o notas…"
          onChange={handleSearchChange}
          size="small"
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} /></InputAdornment>,
          }}
          sx={{ flex: 1, minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Estado</InputLabel>
          <Select value={filterStatus} label="Estado" onChange={e => { setFilterStatus(e.target.value); setPage(0); }}>
            <MenuItem value="">Todos</MenuItem>
            {Object.entries(STATUS_CONFIG).map(([v, c]) => (
              <MenuItem key={v} value={v}>{c.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Desde" type="date" size="small"
          value={dateFrom}
          onChange={e => { setDateFrom(e.target.value); setPage(0); }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        <TextField
          label="Hasta" type="date" size="small"
          value={dateTo}
          onChange={e => { setDateTo(e.target.value); setPage(0); }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        <Button size="small" onClick={() => {
          setFilterStatus(''); setSearch(''); setSearchInputKey(k => k + 1);
          setDateFrom(''); setDateTo(''); setPage(0);
        }}>
          Limpiar filtros
        </Button>
      </Paper>

      {/* Table / mobile cards */}
      {rows.length === 0 ? (
        <Paper sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No hay registros de pago</Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {rows.map(row => {
            const cfg = STATUS_CONFIG[row.status] || { label: row.status, color: 'default' };
            return (
              <Paper key={row.id} sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography
                        variant="body2" fontWeight={600}
                        sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                        onClick={() => navigate(`/patients/${row.patient_id}`)}
                      >
                        {row.patient?.name || `#${row.patient_id}`}
                      </Typography>
                      <Chip label={cfg.label} color={cfg.color} size="small" sx={{ height: 20, fontSize: '0.68rem' }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {new Date(row.created_at + 'Z').toLocaleDateString('es-AR')}
                      {row.amount ? ` · $${parseFloat(row.amount).toLocaleString('es-AR')} ${row.currency}` : ''}
                      {METHOD_LABELS[row.payment_method] ? ` · ${METHOD_LABELS[row.payment_method]}` : ''}
                    </Typography>
                    {row.notes && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                        {row.notes}
                      </Typography>
                    )}
                  </Box>
                  <IconButton size="small" onClick={(e) => openMenu(e, row)} sx={{ flexShrink: 0 }}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>
            );
          })}
          <TablePagination
            component={Paper}
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            labelRowsPerPage="Filas:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </Stack>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Paciente</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Monto</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Método</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Notas</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => {
                  const cfg = STATUS_CONFIG[row.status] || { label: row.status, color: 'default' };
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {new Date(row.created_at + 'Z').toLocaleDateString('es-AR')}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                          onClick={() => navigate(`/patients/${row.patient_id}`)}
                        >
                          {row.patient?.name || `#${row.patient_id}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.amount ? `$${parseFloat(row.amount).toLocaleString('es-AR')} ${row.currency}` : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip label={cfg.label} color={cfg.color} size="small" />
                      </TableCell>
                      <TableCell>{METHOD_LABELS[row.payment_method] || '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="caption" noWrap>{row.notes || '—'}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Acciones">
                          <IconButton size="small" onClick={(e) => openMenu(e, row)}>
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            labelRowsPerPage="Filas:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </Paper>
      )}

      {/* Menú de acciones */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}
        PaperProps={{ sx: { minWidth: 160 } }}>
        <MenuItem dense onClick={() => openEdit(menuRow)}>Editar</MenuItem>
        <MenuItem dense sx={{ color: 'error.main' }} onClick={() => handleDelete(menuRow)}>Eliminar</MenuItem>
      </Menu>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editRow ? 'Editar pago' : 'Registrar pago'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Paciente *</InputLabel>
                <Select value={form.patient_id} label="Paciente *" onChange={set('patient_id')}>
                  {patients.map(p => (
                    <MenuItem key={p.id} value={p.id}>{p.name}{p.dni ? ` — DNI ${p.dni}` : ''}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Monto"
                size="small"
                fullWidth
                type="number"
                value={form.amount}
                onChange={set('amount')}
                placeholder="Ej: 5000"
                InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography> }}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Moneda</InputLabel>
                <Select value={form.currency} label="Moneda" onChange={set('currency')}>
                  <MenuItem value="ARS">ARS</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Estado *</InputLabel>
                <Select value={form.status} label="Estado *" onChange={set('status')}>
                  {Object.entries(STATUS_CONFIG).map(([v, c]) => (
                    <MenuItem key={v} value={v}>{c.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Método de pago</InputLabel>
                <Select value={form.payment_method} label="Método de pago" onChange={set('payment_method')}>
                  <MenuItem value="">— ninguno —</MenuItem>
                  {Object.entries(METHOD_LABELS).map(([v, l]) => (
                    <MenuItem key={v} value={v}>{l}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <TextField
                label="Fecha de pago"
                size="small"
                fullWidth
                type="date"
                value={form.paid_at}
                onChange={set('paid_at')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="Notas"
                size="small"
                fullWidth
                multiline
                rows={2}
                value={form.notes}
                onChange={set('notes')}
                placeholder="Ej: pago parcial, pendiente diferencia..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.patient_id}>
            {saving ? <CircularProgress size={18} /> : editRow ? 'Guardar cambios' : 'Registrar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
